import { cancelSupplement, updateSupplement } from "../common/backend/supplement";
import { produce, Draft } from 'immer'
import { createSelector } from 'reselect'
import { RootState } from ".";

import { Extra, ExtraStock, Option, OptionStock } from "../common/types/supplement";
import Utility, { createPagination } from "../Utility";

type AddSupplementPayload = {
  bookingId: string;
  serviceId: string;
  serviceType: string;
  quantity: number;
};

type CancelSupplementPayload = {
  supplement: any;
  bookingId: string;
};

type State = {
  optionsList: Option[]
  extrasList: Extra[]
  options: {
    [optionId: string]: Option
  }
  extras: {
    [extraId: string]: Extra
  }
  availableOptions: {
    [bookingId: string]: any
  }
  extraStock: {
    [date: string]: {
      [extraId: string]: ExtraStock
    }
  }
  optionStock: {
    [date: string]: {
      [optionId: string]: OptionStock
    }
  }
  allSupplements: {}
  search: string
  currentPage: number,
  added: {
    success: boolean
    bookingId: string
  }
}

const supplement = {
  state: {
    optionsList: [],
    extrasList: [],
    options: {},
    extras: {},
    availableOptions: {},
    extraStock: {},
    optionStock: {},
    allSupplements: {},
    search: "",
    currentPage: 1,
    quantity: {},
    added: {
      success: false,
      bookingId: null,
    },
  } as State,
  reducers: {
    load: produce((draft: Draft<State>, payload: Partial<State>) => {
      Object.assign(draft, payload);

      // Extra list object
      if (payload?.extrasList) {
        draft.extras = payload.extrasList.reduce((acc, current) => {
          return { ...acc, [current.id]: current };
        }, {});
      }

      // Option list object
      if (payload?.optionsList) {
        draft.options = payload.optionsList.reduce((acc, current) => {
          return { ...acc, [current.id]: current };
        }, {});
      }
    }),
    updateSearch: produce((draft: Draft<State>, payload: string) => {
      draft.search = payload;
    }),
    updateCurrentPage: produce((draft: Draft<State>, payload: number) => {
      draft.currentPage = payload;
    }),
    updateAvailableOptions: produce(
      (draft: Draft<State>, payload: { [bookingId: string]: any[] }) => {
        Object.assign(draft.availableOptions, payload);
      }
    ),
  },
  effects: (dispatch) => ({
    async addSupplementAsync(payload: AddSupplementPayload, rootState) {

      const response = await updateSupplement({
        data: [
          {
            bookingFolderId: rootState.booking.bookingFolder.id,
            ...payload,
          },
        ],
      });

      if (response?.data?.data) {
        const {
          bookingExtras,
          bookingOptions,
          bookingAccommodations,
          bookings,
          extraStock,
          optionStock,
        } = response.data.data;
        dispatch.booking.updateBookingsAsync({
          data: {
            bookingExtras,
            bookingOptions,
            bookingAccommodations,
            bookings,
          },
        });
        dispatch.supplement.load({
          extraStock,
          optionStock,
          added: {
            success: true,
            bookingId: payload.bookingId,
          },
        });
      }

      return response;
    },
    async cancelSupplementAsync(
      payload: CancelSupplementPayload,
      rootState: RootState
    ) {
      const response = await cancelSupplement(payload);

      if (response?.data?.success) {
        const { supplement, bookingId } = payload;

        const bookingIndex = rootState.booking.bookings.findIndex(
          (booking) => booking.id === bookingId
        );
        const booking = { ...rootState.booking.bookings[bookingIndex] };

        if (booking) {
          if (supplement?.optionId) {
            booking.bookingOptions = booking.bookingOptions.filter(
              (option) => option.optionId !== supplement.optionId
            );
          }

          if (supplement?.extraId) {
            const removedIds = booking.bookingExtras
              .filter(
                (extra) =>
                  extra.extraId === supplement.extraId &&
                  extra.invoiceId === 0 &&
                  extra.closureId === 0
              )
              .map(extra => extra.id)

 
            booking.bookingExtras = booking.bookingExtras.filter(
              (extra) => !removedIds.includes(extra.id)
            );
          }

          dispatch.booking.updateBookingByIndex({
            bookingIndex,
            booking,
          });

          dispatch.supplement.load({
            extraStock: response.data.data.extraStock,
            optionStock: response.data.data.optionStock,
          });

          return response;
        } else {
          throw new Error("booking not found");
        }
      } else {
        // error
        throw new Error(response?.data?.message);
      }
    },
  }),
};


//const bookingsSelector = (state: RootState) => state.booking.bookings
const bookingFolderSelector = (state: RootState) => state.booking.bookingFolder
const bookingsSelector = (state: RootState) => state.booking.bookings
const extrasSelector = (state: RootState) => state.supplement.extras
const optionsSelector = (state: RootState) => state.supplement.options
const searchSupplementSelector = (state: RootState) => state.supplement.search
const selectedBookingSelector = (state: RootState) => state.booking.selectedBooking
const availableOptions = (state: RootState) => state.supplement.availableOptions
const displayControl = (state: RootState) => state.common.displayControl

// TODO: à optimiser
export const allSupplementsSelector = createSelector (
  [bookingsSelector, extrasSelector, optionsSelector, availableOptions, displayControl],
  (bookings, extras, options, availableOptions, displayControl) => {
    const allSupplements = {}

    bookings.forEach(booking => {
      const supplements = []
      booking.rateplanServices.forEach(service => {
        let unitPrice = service.paxAmounts.find(amounts => amounts.code === "adult");
        const extra = extras[service.serviceId]

        // extras 
        if (service.serviceType === 'extra' && displayControl.addExtraEarlyCheckIn) {
          supplements.push ({
            id: parseInt(service.serviceId),
            name: extra?.name ?? '-',
            description: extra?.description ?? "-",
            image: Utility.imageExists(extra?.photos[0]),
            currency: service.currency,
            paxAmounts: service.paxAmounts,
            price: unitPrice.value,
            quantity: 1,
            totalPrice: unitPrice.value,
            panierTotalPrice: unitPrice.value,
            priceType: service.priceType,
            rateplanId: service.rateplanId,
            type: service.serviceType,
            bookingId: booking.id
          })
        }
      })

      // options
      if (availableOptions?.[booking.id] && displayControl.addOptionEarlyCheckIn) {
        availableOptions[booking.id].forEach(option => {
          const existInBooking = booking.bookingOptions.find(opt => opt.optionId === option.id)
          if (!existInBooking) {
            supplements.push({
              ...option,
              image: Utility.imageExists(options?.[option.id]?.photos[0]),
              type: 'option'
            })
          }
        })
      }
      

      allSupplements[booking.id] = supplements
    });

    return allSupplements
  }
)

export const filtredSupplements = createSelector (
  [allSupplementsSelector, searchSupplementSelector, selectedBookingSelector],
  (supplements, search, selectedBooking) => {
    const filter = new RegExp(search.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim(), 'i')
    const findedSupplements = supplements?.[selectedBooking.id]?.filter(supplement => supplement.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").match(filter));
    if (findedSupplements) {
      createPagination(findedSupplements, 9);
      return findedSupplements.reduce((acc, current) => {
        return {...acc, [current.page]: findedSupplements.filter(suppl => suppl.page === current.page ) }
      }, {})
    } else {
      return {}
    }
  }
)

// calcul info supplement par résa
export const bookingAddedSupplements = createSelector(
  [bookingFolderSelector, bookingsSelector],
  (bookingFolder, bookings) => {
    const bookingAddedSupplements = {}

    // boucle resa
    bookings.forEach(booking => {
      let totalPrice = 0  

      // boucle options
      const bookingOptions = booking?.bookingOptions?.reduce((acc, current) => {

        // note secondaire
        let includAgency = false
        if ( bookingFolder?.noteSecondaire && !current.isNote) {
          includAgency = true
        }

        if (!current.isInclude && !includAgency) totalPrice += current.totalAmount;

        const index = acc.findIndex(
          (option) => option.optionId === current.optionId
        );
        if (index !== -1) {
          const newVar = { ...acc[index] };
          newVar.totalQuantity += current.totalQuantity;
          newVar.totalAmount += current.totalAmount;
          acc.splice(index, 1, newVar);

          return acc;
        } else {
          return [...acc, Object.assign({type: 'option'}, current)];
          return [...acc, current];
        }
      }, []);

      // filtre extra
      let bookingExtras = []
      const bookingExtrasAll = booking?.bookingExtras
      if (bookingExtrasAll) {
        
        const bookingExtrasBilledTotal = bookingExtrasAll
          .filter((extra) => extra.invoiceId !== 0)
          .reduce((acc, current) => {

            // note secondaire
            let includAgency = false
            if ( bookingFolder?.noteSecondaire && !current.isNote) {
              includAgency = true
            }
            if (!includAgency) totalPrice += current.totalAmount;
            //totalPrice += current.totalAmount;

            const index = acc.findIndex(
              (extra) => extra.extraId === current.extraId
            );
            if (index !== -1) {
              const newVar = { ...acc[index] };
              newVar.totalQuantity += current.totalQuantity;
              newVar.totalAmount += current.totalAmount;
              acc.splice(index, 1, newVar);

              return acc;
            } else {
              return [...acc, Object.assign({type: 'option'}, current)];
              // return [...acc, current];
            }
          }, []);

        const bookingExtrasNonBilledTotal = bookingExtrasAll
          .filter((extra) => extra.invoiceId === 0)
          .reduce((acc, current) => {

            // note secondaire
            let includAgency = false
            if ( bookingFolder?.noteSecondaire && !current.isNote) {
              includAgency = true
            }
            if (!includAgency) totalPrice += current.totalAmount;
            //totalPrice += current.totalAmount;

            const index = acc.findIndex(
              (extra) => extra.extraId === current.extraId
            );
            if (index !== -1) {
              const newVar = { ...acc[index] };
              newVar.totalQuantity += current.totalQuantity;
              newVar.totalAmount += current.totalAmount;
              acc.splice(index, 1, newVar);

              return acc;
            } else {
              // return [...acc, current];
              return [...acc, Object.assign({type: 'extra'}, current)];
            }
          }, []);

        bookingExtras = bookingExtrasBilledTotal.concat(
          bookingExtrasNonBilledTotal
        );

      }
      
      bookingAddedSupplements[booking.id] = {
        totalPrice,
        list: bookingOptions?.concat(bookingExtras),
      };
    })

    return bookingAddedSupplements
  }
)

export const bookingAddedSupplementsPerDate = createSelector (
  [bookingsSelector],
  (bookings) => {
    let bookingAddedSupplementsPerDate = {}

    bookings.forEach(booking => {
      let test = {}

      booking.bookingOptions.forEach(current => {
        if (test?.[current.date]) {
          const index = test[current.date].findIndex(option => option.optionId === current.optionId)
          if (index !== -1) {
            const newVar = { ...test[current.date][index] }
            newVar.totalQuantity += current.totalQuantity
            newVar.totalAmount += current.totalAmount
  
            test[current.date].splice(index, 1, newVar)
          } else {
            test[current.date].push(current)
          }
         
        } else {
          test = {
            ...test,
            [current.date]: [current]
          }
        }
      })

      booking.bookingExtras.forEach(current => {
        if (test?.[current.date]) {
          const index = test[current.date].findIndex(extra => extra.extraId === current.extraId)
          if (index !== -1) {
            const newVar = { ...test[current.date][index] }
            newVar.totalQuantity += current.totalQuantity
            newVar.totalAmount += current.totalAmount
  
            test[current.date].splice(index, 1, newVar)
          } else {
            test[current.date].push(current)
          }
          
        } else {
          test = {
            ...test,
            [current.date]: [current]
          }
        }
      })

      bookingAddedSupplementsPerDate[booking.id] = test
    })

    return bookingAddedSupplementsPerDate
  }
)


export default supplement